import React, { useState, useRef } from 'react';
import {
  Button,
  Select,
  Switch,
  Input,
  Upload,
  Tooltip,
  message,
} from 'antd';
import {
  PictureOutlined,
  SendOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { GenerateParams } from '../../../types/aiGenerate';
import {
  ASPECT_RATIO_OPTIONS,
  RESOLUTION_OPTIONS,
  COUNT_OPTIONS,
} from '../../../services/aiGenerate';
import { uploadFileAndGetUrl } from '../../../helpers/file';
import ImageSelector from '../../ImageSelector';
import { useTranslation } from '../../../hooks/useTranslation';
import './index.css';

const { TextArea } = Input;

interface InputPanelProps {
  images: string[];
  text: string;
  params: GenerateParams;
  generating: boolean;
  onImagesChange: (images: string[]) => void;
  onTextChange: (text: string) => void;
  onParamsChange: (params: GenerateParams) => void;
  onGenerate: () => void;
}

const InputPanel: React.FC<InputPanelProps> = ({
  images,
  text,
  params,
  generating,
  onImagesChange,
  onTextChange,
  onParamsChange,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const [showImageSelector, setShowImageSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 更新参数
  const updateParams = (key: keyof GenerateParams, value: any) => {
    onParamsChange({ ...params, [key]: value });
  };

  // 移除图片
  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  // 从历史选择图片
  const handleSelectFromHistory = (selectedUrls: string[]) => {
    onImagesChange([...images, ...selectedUrls]);
  };

  // 本地上传图片
  const handleLocalUpload = async (file: File) => {
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      message.error(t('aiGenerate.pleaseUploadImage'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      message.error(t('aiGenerate.imageSizeLimit'));
      return;
    }

    try {
      message.loading({ content: t('aiGenerate.uploadingImage'), key: 'upload' });
      const url = await uploadFileAndGetUrl(file, 'ai-generate');
      message.success({ content: t('aiGenerate.uploadImageSuccess'), key: 'upload' });
      onImagesChange([...images, url]);
    } catch (error: any) {
      message.error({ content: t('aiGenerate.uploadFailedWithMessage', { message: error.message }), key: 'upload' });
    }
  };

  // 处理文件选择
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(handleLocalUpload);
    }
    // 重置 input，允许选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 处理发送
  const handleSend = () => {
    if (generating) return;
    onGenerate();
  };

  // 处理按键
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="input-panel">
      {/* 已选择的图片预览 */}
      {images.length > 0 && (
        <div className="input-images-preview">
          {images.map((url, index) => (
            <div key={index} className="preview-image-item">
              <img src={url} alt={`图片 ${index + 1}`} />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                className="remove-image-btn"
                onClick={() => handleRemoveImage(index)}
              />
              <div className="preview-image-name">
                图片 {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 参数配置区域 */}
      <div className="input-params">
        <div className="param-item">
          <span className="param-label">
            {t('aiGenerate.model')}:
            <Tooltip title={t('aiGenerate.selectModel')}>
              <QuestionCircleOutlined className="param-help" />
            </Tooltip>
          </span>
          <Select
            value={params.model}
            onChange={(value) => updateParams('model', value)}
            size="small"
            style={{ width: 140 }}
            options={[
              { value: 'jimeng-4.0', label: '即梦 4.0' },
            ]}
          />
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.generateCount')}:</span>
          <Select
            value={params.count}
            onChange={(value) => updateParams('count', value)}
            size="small"
            style={{ width: 70 }}
            options={COUNT_OPTIONS.map((n) => ({ value: n, label: `${n}张` }))}
          />
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.imageSize')}:</span>
          <div className="aspect-ratio-options">
            {ASPECT_RATIO_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="small"
                type={params.aspectRatio === option.value ? 'primary' : 'default'}
                onClick={() => updateParams('aspectRatio', option.value)}
                className="aspect-ratio-btn"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.resolution')}:</span>
          <Select
            value={params.resolution}
            onChange={(value) => updateParams('resolution', value)}
            size="small"
            style={{ width: 70 }}
            options={RESOLUTION_OPTIONS}
          />
        </div>

        <div className="param-item">
          <span className="param-label">
            {t('aiGenerate.continuousEdit')}:
            <Tooltip title={t('aiGenerate.continuousEditTip')}>
              <QuestionCircleOutlined className="param-help" />
            </Tooltip>
          </span>
          <Switch
            size="small"
            checked={params.continuousMode}
            onChange={(checked) => updateParams('continuousMode', checked)}
          />
        </div>
      </div>

      <div className="input-hint">
        {t('aiGenerate.inputHint')}
      </div>

      {/* 输入区域 */}
      <div className="input-area">
        <div className="input-actions">
          <Tooltip title={t('aiGenerate.uploadLocalImage')}>
            <Button
              type="text"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            />
          </Tooltip>
          <Tooltip title={t('aiGenerate.selectFromHistory')}>
            <Button
              type="text"
              icon={<FolderOpenOutlined />}
              onClick={() => setShowImageSelector(true)}
            />
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        <div className="input-textarea-wrapper">
          <TextArea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aiGenerate.promptPlaceholder')}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={2000}
            showCount
            className="input-textarea"
          />
        </div>

        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={generating}
          disabled={generating || (!text.trim() && images.length === 0)}
          className="send-btn"
        />
      </div>

      {/* 图片选择器弹窗 */}
      <ImageSelector
        open={showImageSelector}
        onClose={() => setShowImageSelector(false)}
        onSelect={handleSelectFromHistory}
        currentImages={images}
      />
    </div>
  );
};

export default InputPanel;
